import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Breadcrumbs, Typography, Link } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from '@contexts/SnackbarContext';
import API from '@utils/axios';
import Layout from '@components/Layout';
import WorkflowForm from '@components/workflow/WorkflowForm';
import WorkflowLoading from '@components/workflow/WorkflowLoading';
import defaultBpmn from '@data/default-bpmn';

function WorkflowCreatePage({ type }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();
    const [initialData, setInitialData] = useState({bpmn: defaultBpmn});
    const [loaded, setLoaded] = useState(false);
    const [searchParams] = useSearchParams();
    const template = searchParams.get('template');

    const fetchData = useCallback(async () => {
        if (template === null) {
            setInitialData(prevData => ({
                ...prevData,
                bpmn: defaultBpmn,
            }));
            setLoaded(true);
            return;
        } else {
            try {
                const response = await API.get(`/workflows/${template}`);
                setInitialData(prevData => ({
                    ...prevData,
                    bpmn: response.data.bpmn,
                }));
            } catch (error) {
                showMessage(t('Failed to load data'), 'error');
            } finally {
                setLoaded(true);
            }
        }
    }, [template, t, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setInitialData(prevData => ({
            ...prevData,
            customer_id: type === 'custom' ? '-' : '',
        }));
    }, [type]);

    const handleSubmit = async (data) => {
        if (data.customer_id === '-') {
            showMessage(t('Please select a customer'), 'error');
            return;
        }

        const showWarnings = (warnings) => {
            warnings.forEach(warning => {
                if (typeof warning === 'string') {
                    showMessage(t(warning), 'warning');
                } else if (typeof warning === 'object' && warning.message) {
                    showMessage(t(warning.message, warning.data || {}), 'warning');
                }
            });
        }

        try {
            const result = await API.post(`/workflows`, data);
            
            // Affichage du message de succès
            showMessage(t('Workflow created successfully'), 'success');
            
            // Affichage des warnings s'il y en a
            showWarnings(result.data.warnings);
            
            navigate(`/workflows/${type}/${result.data.id}`);
        } catch (error) {
            if (error.response && error.response.status === 422 && error.response.data.message) {
                showMessage(t('Unable to save the workflow:')  + ' ' + t(error.response.data.message), 'error');
            } else {
                showMessage(error.message, 'error');
            }
            showWarnings(error.response.data.warnings);
        }
    };

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to={`/workflows/${type}`} underline="hover">{t('Workflow management')}</Link>
                    {type === 'custom'
                        ? <Link component={RouterLink} to="/workflows/custom" underline="hover">{t('Custom workflows')}</Link>
                        : <Link component={RouterLink} to="/workflows/templates" underline="hover">{t('Templates')}</Link>
                    }
                    {type === 'custom'
                        ? <Typography>{t('Create custom workflow')}</Typography>
                        : <Typography>{t('Create workflow template')}</Typography>
                    }
                    
                </Breadcrumbs>
                <Link component={RouterLink} to={`/workflows/${type}`} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            {loaded ?
                <WorkflowForm initialData={initialData} handleSubmit={handleSubmit} />
                : <WorkflowLoading />
            }
        </Layout>
    );
}

export default WorkflowCreatePage;
